import { Settings } from 'luxon';
import { type JSX } from 'react';
import { createRoot } from 'react-dom/client';
import ArtviseChat from './components/artvise-chat/artvise-chat';
import LogService from './react/services/log-service/log.service';

// React components
import BlogList from './react/components/BlogList';
import LostAndFound, { ILostAndFoundSettings, ILostAndFoundTexts } from './react/components/LostAndFound';
import CookieConsentModal, { ICookieConsentModalSettings, ICookieConsentModalTexts } from './react/components/CookieConsentModal';
import QuickCookieConsentModal, { IQuickCookieConsentModalSettings, IQuickCookieConsentModalTexts } from './react/components/QuickCookieConsentModal';
import TicketPrices, { ITicketPricesSettings, ITicketPricesTexts } from './react/components/TicketPrices';
import TrafficInformationOverview, { ITrafficInformationOverviewTexts } from './react/components/TrafficInformation/TrafficInformationOverview';
import TrafficInformationSearch, { ITrafficInformationSearchSettings, ITrafficInformationSearchTexts } from './react/components/TrafficInformation/TrafficInformationSearch';
import DepartureBoardLinkGenerator from './react/components/DepartureBoardLinkGenerator';
import PressPage from './react/components/Press';
import StopAreaSearchDetails from './react/components/StopAreas/StopAreaSearchDetails';
import StopAreaSearch from './react/components/StopAreas/StopAreaSearch';
import Timetables from './react/components/Timetables';
import TicketInspectionSurchargeForm from './react/components/TicketInspectionSurchargeForm';
import SiteSearchDesktop from './react/components/SiteSearchDesktop';
import SearchPage, { ISearchPageSettings, ISearchPageTexts } from './react/components/Search';
import Reclamation from './react/components/Reclamation';
import SingleTickets from './react/components/SingleTickets';
import AreaTicketDetails, { IAreaTicketDetailsSettings, IAreaTicketDetailsTexts } from './react/components/AreaTickets/AreaTicketDetails';
import SiteSearchMobile from './react/components/SiteSearchMobile';
import NextDeparture, { INextDepartureSettings, INextDepartureTexts } from './react/components/NextDeparture';
import AreaTicketSearch from './react/components/AreaTickets/AreaTicketSearch';
import QuestionsAndOpinions from './react/components/QuestionsAndOpinions';
import CertificateCardForm from './react/components/CertificateCardForm';
import TravelPlanner from './react/components/TravelPlanner';
import DelayCompensation from './react/components/DelayCompensation/DelayCompensationVasttrafik';
import DelayCompensationOresundstag from './react/components/DelayCompensation/DelayCompensationOresundstag';
import { TranslationService } from './react/services/translation.service';
import { ConfigurationService } from './react/services/configuration.service';
import Scoreboard, { IScoreboardSettings, IScoreboardTexts } from './react/components/PaEgnaBen/Scoreboard';
import PriceCalculatorPage from './react/components/PriceCalculatorPage';
import TravelAssistanceCardForm from './react/components/TravelAssistanceCardForm';
import StopAreaList from './react/components/StopAreas/StopAreaList';
import StralkastarenRegistration from './react/components/Partnerportalen/StralkastarenRegistration';
import StralkastarenUnregistration from './react/components/Partnerportalen/StralkastarenUnregistration';

import createPaEgnaBenAdminstratorRouterProvider from './react/components/PaEgnaBen/Administrator/administrator.router-provider-factory';
import createPaEgnaBenTeacherRouterProvider from './react/components/PaEgnaBen/Teacher/teacher.router-provider-factory';
import createPaEgnaBenMunicipalityAdministratorRouterProvider from './react/components/PaEgnaBen/MunicipalityAdministrator/municipalityAdministrator.router-provider-factory';
import createPaEgnaBenParticipationOverviewRouterProvider from './react/components/PaEgnaBen/ParticipationOverview/ParticipationOverview.router-provider-factory';
import createStralkastarenAdministratorRouterProvider from './react/components/Partnerportalen/StralkastarenAdministrator/stralkastaren-administrator.router-provider-factory';
import createMittKontoPrivateCustomerRouterProvider, { IPrivateCustomerSettings } from './react/components/MittKonto/PrivateCustomer/create-mitt-konto-private-customer.router-provider-factory';
import CreateAccount, { ICreateAccountTexts } from './react/components/MittKonto/CreateAccount';

import LocalServiceMap, { ILocalServiceMapSettings, ILocalServiceMapTexts } from './react/components/LocalServiceMap';
import MunicipalitySelectorModal from './react/components/PaEgnaBen/MunicipalitySelectorModal';
import DelayCompensationTransportWarranty from './react/components/DelayCompensation/DelayCompensationTransportWarranty';
import { ClientSideStorageService } from './react/services/client-side-storage.service';
import SingleQuestionFeedbackForm, { ISingleQuestionFeedbackFormSettings, ISingleQuestionFeedbackFormTexts } from './react/components/Forms/SingleQuestionFeedbackForm';
import ContactForm, { IContactFormTexts, IContactFormSettings } from './react/components/Forms/ContactForm';
import OrderTicketInspectionForm, { IOrderTicketInspectionFormTexts, IOrderTicketInspectionFormSettings } from './react/components/Forms/OrderTicketInspectionForm';
import { ServerSideTextCollection } from './react/components/Shared/Base/server-side-text-collection';
import { ServerSideSettingsCollection } from './react/components/Shared/Base/server-side-settings-collection';
import MyStopAreas, { IMyStopAreasSettings, IMyStopAreasTexts } from './react/components/Labs/MyStopAreas';
import MultipleYesNoFeedbackForm, { IMultipleYesNoFeedbackFormSettings, IMultipleYesNoFeedbackFormTexts } from './react/components/Forms/MultipleYesNoFeedbackForm';
import { ISurveyModalFormSettings, ISurveyModalFormTexts } from './react/components/Forms/SurveyModalForm/survey-modal-form.interface';
import SurveyModalForm from './react/components/Forms/SurveyModalForm';
import TrafficInformationDetailsV2, { ITrafficInformationDetailsV2Settings, ITrafficInformationDetailsV2Texts } from './react/components/TrafficInformation/TrafficInformationDetailsV2';
import VehiclePositionsMap, { IVehiclePositionsMapSettings, IVehiclePositionsMapTexts } from './react/components/Labs/VehiclePositionsMap';
import Refund, { IRefundSettings, IRefundTexts } from './react/components/Refund';

LogService.init();

new ArtviseChat();

TranslationService.init();

Settings.defaultZone = 'Europe/Stockholm';

ClientSideStorageService.handleMatomoConsent();

const { lang } = document.getElementsByTagName('html')[0];
if (lang) {
  Settings.defaultLocale = lang;
}

function render(id: string, node: React.ReactNode) {
  if (document.getElementById(id) != null) {
    createRoot(document.getElementById(id) as Element).render(node);
  }
}

const dataReactComponentRegistry: Record<string, (settings: unknown, texts: unknown) => JSX.Element> = {
  'single-question-feedback-form': (settings, texts) => <SingleQuestionFeedbackForm settings={settings as ISingleQuestionFeedbackFormSettings} texts={texts as ISingleQuestionFeedbackFormTexts} />,
  'contact-form': (settings, texts) => <ContactForm settings={settings as IContactFormSettings} texts={texts as IContactFormTexts} />,
  'order-ticket-inspection-form': (settings, texts) => <OrderTicketInspectionForm settings={settings as IOrderTicketInspectionFormSettings} texts={texts as IOrderTicketInspectionFormTexts} />,
  'lost-and-found': (settings, texts) => <LostAndFound settings={settings as ILostAndFoundSettings} texts={texts as ILostAndFoundTexts} />,
  'my-stop-areas': (settings, texts) => <MyStopAreas settings={settings as IMyStopAreasSettings} texts={texts as IMyStopAreasTexts} />,
  'multiple-yes-no-feedback-form': (settings, texts) => <MultipleYesNoFeedbackForm settings={settings as IMultipleYesNoFeedbackFormSettings} texts={texts as IMultipleYesNoFeedbackFormTexts} />,
  'survey-modal-form': (settings, texts) => <SurveyModalForm settings={settings as ISurveyModalFormSettings} texts={texts as ISurveyModalFormTexts} />,
  'traffic-information-search': (settings, texts) => <TrafficInformationSearch settings={settings as ITrafficInformationSearchSettings} texts={texts as ITrafficInformationSearchTexts} />,
  'next-departure': (settings, texts) => <NextDeparture settings={settings as INextDepartureSettings} texts={texts as INextDepartureTexts} />,
  'cookie-consent-modal': (settings, texts) => <CookieConsentModal settings={settings as ICookieConsentModalSettings} texts={texts as ICookieConsentModalTexts} />,
  'quick-cookie-consent-modal': (settings, texts) => <QuickCookieConsentModal settings={settings as IQuickCookieConsentModalSettings} texts={texts as IQuickCookieConsentModalTexts} />,
  'local-service-map-block': (settings, texts) => <LocalServiceMap settings={settings as ILocalServiceMapSettings} texts={texts as ILocalServiceMapTexts} />,
  'traffic-situation-details-v2': (settings, texts) => <TrafficInformationDetailsV2 settings={settings as ITrafficInformationDetailsV2Settings} texts={texts as ITrafficInformationDetailsV2Texts} />,
  'pa-egna-ben-scoreboard': (settings, texts) => <Scoreboard settings={settings as IScoreboardSettings} texts={texts as IScoreboardTexts} />,
  'vehicle-positions-map': (settings, texts) => <VehiclePositionsMap settings={settings as IVehiclePositionsMapSettings} texts={texts as IVehiclePositionsMapTexts} />,
  'ticket-prices': (settings, texts) => <TicketPrices settings={settings as ITicketPricesSettings} texts={texts as ITicketPricesTexts} />,
  'traffic-information-overview': (_settings, texts) => <TrafficInformationOverview texts={texts as ITrafficInformationOverviewTexts} />,
  'area-ticket-details': (settings, texts) => <AreaTicketDetails settings={settings as IAreaTicketDetailsSettings} texts={texts as IAreaTicketDetailsTexts} />,
  'refund-form': (settings, texts) => <Refund settings={settings as IRefundSettings} texts={texts as IRefundTexts} />,
  'mitt-konto-private-customer': (settings) => createMittKontoPrivateCustomerRouterProvider(settings as IPrivateCustomerSettings),
  'mitt-konto-create-account': (_settings, texts) => <CreateAccount texts={texts as ICreateAccountTexts} />,
  'search-page': (settings, texts) => <SearchPage settings={settings as ISearchPageSettings} texts={texts as ISearchPageTexts} />,
};

function renderDataReactComponents() {
  const elements = document.querySelectorAll('[data-react-component]') as NodeListOf<HTMLElement>;
  elements.forEach((element) => {
    const componentName = element.getAttribute('data-react-component');
    if (!componentName) {
      return;
    }
    try {
      const settingsJson = element.dataset.settings;
      const textsJson = element.dataset.texts;

      const settings = settingsJson ? (JSON.parse(settingsJson) as ServerSideSettingsCollection) : {};
      const texts = textsJson ? (JSON.parse(textsJson) as ServerSideTextCollection) : {};
      const componentFactory = dataReactComponentRegistry[componentName];

      if (!componentFactory) {
        LogService.logError(new Error(`Unknown component: ${componentName}`), `Component ${componentName} not found in registry`);
        return;
      }

      createRoot(element).render(componentFactory(settings, texts));

      delete element.dataset.settings;
      delete element.dataset.reactComponent;
      delete element.dataset.texts;
    } catch (error) {
      LogService.logError(error, `Failed to render component ${componentName}`);
    }
  });
}

function renderRouter(id: string, createRouterProviderFunc: () => JSX.Element) {
  if (document.getElementById(id) != null) {
    createRoot(document.getElementById(id) as Element).render(createRouterProviderFunc());
  }
}

render('blog-list', <BlogList />);
render('departureboardlinkgeneratorpage', <DepartureBoardLinkGenerator />);
render('press-page', <PressPage />);
render('stopareasearchdetailspage', <StopAreaSearchDetails />);
render('timetables', <Timetables />);
render('stopareasearchpage', <StopAreaSearch />);
render('ticket-inspection-surcharge-appeal-form', <TicketInspectionSurchargeForm />);

render('dayticketspage', <AreaTicketSearch productType="dayticket" />);
render('periodticketspage', <AreaTicketSearch productType="periodticket" />);
render('reclamationform', <Reclamation />);
render('single-ticket-search', <SingleTickets />);
render('site-search-desktop-placeholder', <SiteSearchDesktop />);
render('questionsandopinions', <QuestionsAndOpinions />);
render('site-search-form-mobile', <SiteSearchMobile />);
render('certificate-card-form', <CertificateCardForm />);
render('travel-planner-startpage', <TravelPlanner initialShowSearchDetails={false} headingText={TranslationService.get('travelplanner.heading')} />);
render('travel-planner-page', <TravelPlanner initialShowSearchDetails={true} headerHtml={ConfigurationService.get('travelplannerpage.header')} />);
render('delaycompensationform', <DelayCompensation />);
render('delaycompensationoresundstagform', <DelayCompensationOresundstag />);
render('delaycompensationTransportWarrantyForm', <DelayCompensationTransportWarranty />);
render('municipality-selector-modal', <MunicipalitySelectorModal />);
render('stop-area-list', <StopAreaList />);
render('stralkastaren-registration', <StralkastarenRegistration />);
render('stralkastaren-unregistration', <StralkastarenUnregistration />);
renderRouter('stralkastaren-administrator', createStralkastarenAdministratorRouterProvider);

render('price-calculator-page', <PriceCalculatorPage />);
render('travel-assistance-card-form', <TravelAssistanceCardForm />);
renderRouter('pa-egna-ben-admin', createPaEgnaBenAdminstratorRouterProvider);
renderRouter('pa-egna-ben-teacher', createPaEgnaBenTeacherRouterProvider);
renderRouter('pa-egna-ben-municipality-administrator', createPaEgnaBenMunicipalityAdministratorRouterProvider);
renderRouter('pa-egna-ben-participation-overview', createPaEgnaBenParticipationOverviewRouterProvider);
renderDataReactComponents();

